import { Component, DestroyRef, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { BillingAddress, BillingAddressInput, BillingAddressSelection } from '../../models/billing-address.model';
import { BillingAddressService } from '../../services/billing-address.service';
import { ToastService } from '../../services/notification/toast.service';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { billingAddressErrorMessage, hasValidAddressId } from '../utils/billing-address-http.util';
import { formatAddressDetail } from '../utils/card-display.util';
import { BillingAddressFormComponent } from '../billing-address-form/billing-address-form.component';

const COMBINING_MARKS = /[̀-ͯ]/g;

/** El buscador solo aparece con más direcciones que este umbral. */
export const SEARCH_THRESHOLD = 5;

export interface AddressRow {
  key: string;
  address: BillingAddress;
  selected: boolean;
  isCurrent: boolean;
  usedByOthers: number;
}

function normalizeText(text: string | null | undefined): string {
  return String(text ?? '').normalize('NFD').replace(COMBINING_MARKS, '').toLowerCase().trim();
}

let nextGroupId = 0;

@Component({
  selector: 'app-billing-address-picker',
  templateUrl: './billing-address-picker.component.html',
  styleUrls: ['./billing-address-picker.component.scss'],
  standalone: true,
  imports: [FormsModule, TranslateModule, BillingAddressFormComponent]
})
export class BillingAddressPickerComponent implements OnChanges {
  @Input() addresses: BillingAddress[] = [];
  @Input() currentAddressId: number | null = null;
  /** Tarjeta actual: se excluye al contar "usada por otras tarjetas". */
  @Input() cardId: number | null = null;
  /** Cliente al que se asocian las direcciones nuevas. */
  @Input() customerId: number | string = '';
  /** Dirección marcada al recibir la lista (sin emitir selectionChange); no es "actual" ni muestra tag. */
  @Input() initialSelectedId: number | null = null;
  /** Abre el formulario de nueva dirección al recibir una lista vacía (primer ngOnChanges de addresses). */
  @Input() autoOpenForm = false;

  /** Dirección existente seleccionada, o null si no hay selección. */
  @Output() selectionChange = new EventEmitter<BillingAddressSelection | null>();
  /** Dirección creada (o reusada) en el servidor desde el formulario inline. */
  @Output() addressCreated = new EventEmitter<BillingAddress>();

  /** true mientras el formulario inline está abierto o enviando: el padre no debe permitir guardar. */
  @Output() busyChange = new EventEmitter<boolean>();

  readonly groupName = `billingAddress-${nextGroupId++}`;
  query = '';
  showForm = false;
  submitting = false;
  selectedId: number | null = null;

  constructor(
    private billingAddressService: BillingAddressService,
    private destroyRef: DestroyRef,
    private spinner: SpinnerService,
    private toast: ToastService,
    private translate: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.autoOpenForm && changes['addresses']?.firstChange && this.addresses.length === 0) {
      // Se abre sin emitir busyChange síncrono (estamos en la detección de cambios del padre): se difiere a microtask.
      this.showForm = true;
      Promise.resolve().then(() => this.emitBusy());
    }
    if (changes['addresses'] && this.selectedId !== null && !this.addresses.some(a => a.id === this.selectedId)) {
      this.selectedId = null;
      // Diferido: emitir síncrono durante el CD del padre provoca NG0100.
      Promise.resolve().then(() => this.selectionChange.emit(null));
    }
    if (this.selectedId !== null) {
      return;
    }
    // Marca visualmente sin emitir selectionChange: no es un cambio del usuario.
    const marked = this.initialSelectedId ?? this.currentAddressId;
    if (marked !== null && this.addresses.some(a => a.id === marked)) {
      this.selectedId = marked;
    }
  }

  /** Dirección actual primero; el resto conserva el orden recibido. */
  get orderedAddresses(): BillingAddress[] {
    const current = this.addresses.filter(a => a.id === this.currentAddressId);
    const others = this.addresses.filter(a => a.id !== this.currentAddressId);
    return [...current, ...others];
  }

  /** Filtro client-side por address, city y zipCode (sin distinguir mayúsculas ni acentos). */
  get filteredAddresses(): BillingAddress[] {
    const needle = this.showSearch ? normalizeText(this.query) : '';
    if (!needle) {
      return this.orderedAddresses;
    }
    return this.orderedAddresses.filter(a =>
      [a.address, a.city, a.zipCode].some(field => normalizeText(field).includes(needle))
    );
  }

  get showSearch(): boolean {
    return this.addresses.length > SEARCH_THRESHOLD;
  }

  /** Una sola lista: actual primero, resto (filtrado por el buscador). */
  get rows(): AddressRow[] {
    return this.filteredAddresses.map((a): AddressRow => ({
      key: `id-${a.id}`,
      address: a,
      selected: this.selectedId === a.id,
      isCurrent: this.isCurrent(a),
      usedByOthers: this.usedByOthersCount(a)
    }));
  }

  selectRow(row: AddressRow): void {
    this.selectAddress(row.address);
  }

  isCurrent(address: BillingAddress): boolean {
    return address.id === this.currentAddressId;
  }

  /** Cantidad de otras tarjetas (excluye la actual) que usan la dirección. */
  usedByOthersCount(address: BillingAddress): number {
    return (address.cardsInUse ?? []).filter(id => id !== this.cardId).length;
  }

  /** Clave i18n del tag "usada por": singular con 1 tarjeta. */
  usedByKey(count: number): string {
    return count === 1 ? 'customerCards.usedByOneCard' : 'customerCards.usedByOtherCards';
  }

  detail(address: BillingAddress): string {
    return formatAddressDetail(address);
  }

  selectAddress(address: BillingAddress): void {
    this.selectedId = address.id;
    this.selectionChange.emit({ addressId: address.id });
  }

  get busy(): boolean {
    return this.showForm || this.submitting;
  }

  openForm(): void {
    this.showForm = true;
    this.emitBusy();
  }

  onFormCancel(): void {
    if (this.submitting) {
      return;
    }
    this.showForm = false;
    this.emitBusy();
  }

  /** Crea la dirección en el servidor (201 creada / 200 reusada) y la selecciona. */
  onFormSaved(input: BillingAddressInput): void {
    if (this.submitting) {
      return;
    }
    if (!this.hasCustomerId()) {
      this.toast.showError(this.translate.instant('customerCards.errorGeneric'));
      return;
    }
    this.submitting = true;
    this.emitBusy();
    const lease = this.spinner.acquire();
    this.billingAddressService.create(this.customerId, input).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => {
        this.submitting = false;
        lease.release();
        this.emitBusy();
      })
    ).subscribe({
      next: created => {
        if (!hasValidAddressId(created)) {
          this.toast.showError(this.translate.instant('customerCards.errorGeneric'));
          return;
        }
        this.onCreated(created);
      },
      error: err => this.toast.showError(billingAddressErrorMessage(err, this.translate))
    });
  }

  private lastBusy = false;

  private emitBusy(): void {
    if (this.busy !== this.lastBusy) {
      this.lastBusy = this.busy;
      this.busyChange.emit(this.busy);
    }
  }

  private hasCustomerId(): boolean {
    return String(this.customerId ?? '').trim() !== '';
  }

  private onCreated(created: BillingAddress): void {
    if (!this.addresses.some(a => a.id === created.id)) {
      this.addresses = [...this.addresses, created];
    }
    this.showForm = false;
    this.selectAddress(created);
    this.addressCreated.emit(created);
    this.toast.showSuccess(this.translate.instant('customerCards.addressCreated'));
  }
}
